import { Container, Section, Stack } from '@grubhubprod/gh-cookbook-react/dist';
import { subscribe } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import { Address } from '@grubhubprod/order-taking-client-sdk/src/sdk/types/geocode';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AccountCorporateSection } from 'src/components/Account/AccountCorporateSection';
import { BalanceInvoicesSection } from 'src/components/Account/BalanceInvoicesSection/BalanceInvoicesSection';
import { AccountPaymentSection } from 'src/components/Account/AccountPaymentSection/AccountPaymentSection';
import { EditCorpModal } from 'src/components/Account/EditCorpModal';
import { AddressConfirmationModal } from 'src/components/AddressConfirmationModal/AddressConfirmationModal';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import {
  pushError,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';
import { sdk } from 'src/utilities/sdk';
import { PageHeader } from 'src/components/selfService/layout/PageHeader/PageHeader';
import { PageTitle } from 'src/components/selfService/layout/PageTitle/PageTitle';
import { BalanceProvider } from 'src/components/selfService/common/Balance/context/BalanceProvider';
import { useBalanceAccountInfo } from 'src/components/selfService/common/Balance/hooks/useBalanceAccountInfo';
import { NetTermsPromotionSection } from 'src/components/selfService/common/Balance/sections/NetTermsPromotionSection/NetTermsPromotionSection';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FeatureFlag } from 'src/types/FeatureFlag';
import { SelfServiceCorp } from 'src/types/Corp';
import { useSelfServiceCorporateDataContext } from 'src/components/selfService/common/SelfServiceCorporateData/hooks/useSelfServiceCorporateDataContext';

export default function AccountPage(): JSX.Element {
  const { corpid: corpId } = useParams<{ corpid: string }>();

  const { corpData, isLoading, refetch } = useSelfServiceCorporateDataContext();

  const {
    enterpriseData: { isInternalAdminUser },
  } = useAuth();

  const [corp, setCorp] = useState<SelfServiceCorp>(null);
  const { isBalanceAccount } = useBalanceAccountInfo();

  const [showEditCorpModal, setShowEditCorpModal] = useState({
    showModal: false,
    corpInfo: null,
  });
  const [addressConfirmationModal, setAddressConfirmationModal] = useState({
    showModal: false,
    suggestedGeoAddresses: [],
    currentAddressString: '',
  });

  const { isLoading: isBalanceUiLoading, value: isBalanceUiEnabled } =
    useFeatureFlag(FeatureFlag.ENABLE_BALANCE_UI);

  const { sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    sendPageViewEvent();
  }, []);

  useEffect(() => {
    if (!corpData || isLoading) return;

    const fetchAdminAndSetCorp = async () => {
      try {
        const { first_name, last_name, email } = await sdk.http.get(
          `/corp-admin/${corpId}/diners/${corpData.dinerId}`
        );
        setCorp({
          name: corpData.name,
          diner_id: corpData.dinerId,
          business_email: corpData.businessEmail,
          location: corpData.location,
          adminInfo: { first_name, last_name, email },
          utm_details: corpData.utmDetails,
        });
      } catch (error) {
        pushErrorFromAPI(error);
      }
    };

    fetchAdminAndSetCorp();
  }, [corpData, corpId, isLoading]);

  useEffect(() => {
    const subscription = subscribe(
      'showAddressConfirmationModal',
      ({
        corpInfo,
        suggestedGeoAddresses,
      }: {
        corpInfo: SelfServiceCorp;
        suggestedGeoAddresses: Address[];
      }) => {
        showAddressConfirmationModal(corpInfo, suggestedGeoAddresses);
        setShowEditCorpModal({ showModal: false, corpInfo });
      }
    );

    return () => {
      subscription();
    };
  }, []);

  function sendPageViewEvent() {
    sendTealiumPageView({
      pageGroup: 'corporate self service - account management',
      pageName: 'Account Details',
    });
  }

  function onEditCorpSuccess() {
    refetch();
    setShowEditCorpModal({ showModal: false, corpInfo: corp });
    pushSuccess("Your company's details have been updated");
  }

  async function onEditCorpError() {
    pushError(
      "There was a problem updating your company's details. Try again."
    );
  }

  function onAddressConfirmationSuccess(address: Address) {
    setAddressConfirmationModal({
      showModal: false,
      currentAddressString: '',
      suggestedGeoAddresses: null,
    });

    const {
      address_region: state,
      address_locality: city,
      address_sublocality: city2,
      street_address1: address1,
      street_address2: address2,
      postal_code: zip,
      latitude,
      longitude,
    } = address;

    const { location } = showEditCorpModal.corpInfo;

    const corpInfo: SelfServiceCorp = {
      ...showEditCorpModal.corpInfo,
      location: {
        phone: location.phone,
        state,
        city: city || city2,
        address: address1,
        floor: address2 || location.floor,
        zip,
        latitude,
        longitude,
      },
    };

    setShowEditCorpModal({ corpInfo, showModal: true });
  }

  function onAddressConfirmationCancel() {
    setShowEditCorpModal({ ...showEditCorpModal, showModal: true });
    setAddressConfirmationModal({
      showModal: false,
      currentAddressString: '',
      suggestedGeoAddresses: null,
    });
  }

  function showAddressConfirmationModal(
    corpInfo: SelfServiceCorp,
    suggestedGeoAddresses: Address[]
  ) {
    const { address, city, state, zip } = corpInfo.location;
    setAddressConfirmationModal({
      showModal: true,
      suggestedGeoAddresses,
      currentAddressString: `${address}, ${city}, ${state}, ${zip}`,
    });
  }

  const pageHeaderY = isBalanceUiLoading || !isBalanceUiEnabled ? 'sm' : 'md';

  return (
    <>
      <Container variant="default" className="accountPage">
        <Section inset="md">
          <PageHeader y={pageHeaderY}>
            <PageTitle title="Your account" />
          </PageHeader>
          <BalanceProvider>
            {isBalanceAccount && <NetTermsPromotionSection />}
            {corp && (
              <Stack y="md">
                <AccountCorporateSection
                  corp={corp}
                  onEditClick={() =>
                    setShowEditCorpModal({
                      showModal: true,
                      corpInfo: corp,
                    })
                  }
                />
              </Stack>
            )}

            {isBalanceAccount && (
              <Stack y="md">
                <BalanceInvoicesSection />
              </Stack>
            )}

            <AccountPaymentSection
              corpId={corpId as string}
              isInternalAdminUser={isInternalAdminUser}
            />
          </BalanceProvider>
        </Section>
        {showEditCorpModal.showModal && (
          <EditCorpModal
            corpInfo={showEditCorpModal.corpInfo}
            onError={onEditCorpError}
            corpId={corpId as string}
            onCloseModal={() =>
              setShowEditCorpModal({ showModal: false, corpInfo: null })
            }
            onSuccess={onEditCorpSuccess}
          />
        )}

        {addressConfirmationModal.showModal && (
          <AddressConfirmationModal
            isAccount
            currentAddressString={addressConfirmationModal.currentAddressString}
            suggestedGeoAddresses={
              addressConfirmationModal.suggestedGeoAddresses
            }
            onSuccess={onAddressConfirmationSuccess}
            onClose={onAddressConfirmationCancel}
          />
        )}
      </Container>
    </>
  );
}
